import React, { useState, useRef } from 'react';
import { ChevronLeft, ChevronRight, Calendar, Users, AlertTriangle, TrendingDown, Shield, X, Volume2, Award, Search, Zap, Ship, FileText, Globe, BookOpen } from 'lucide-react';


const InteractiveTimeline = () => {
  const [selectedEvent, setSelectedEvent] = useState(null);
  const scrollRef = useRef(null);

  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
  };

  // Comprehensive timeline with 20+ major events
  const timelineEvents = [
    {
      year: '1950',
      title: 'Species Discovered',
      description: 'Vaquita first described by Norris & McFarland',
      type: 'discovery',
      icon: Calendar,
      image: 'https://upload.wikimedia.org/wikipedia/commons/d/d5/Vaquita_swimming_through_the_void._%28Phocoena_Sinus%29.png',
      details: 'The porpoise, vaquita, was formally described to science by Kenneth S. Norris and William N. McFarland based solely on skulls found in a beach in Mexico on March 18, 1950. In the paper,"A New Harbor Porpoise of the Genus Phocoena from the Gulf of California" by Kenneth S.Norris and William N. McFarland, they were described as "slightly less than 5 feet in length. Each animal was a uniform brown color dorsally and had a small triangular dorsal fin."',
      significance: 'Scientific recognition of new species',
      population: 'Unknown (estimated 500-600)',
      location: 'Gulf of California, Mexico',
      impact: 'Established vaquita as distinct species',
      color: 'bg-blue-600',
      attribution: '좀비 브렌다, CC BY-SA 4.0, via Wikimedia Commons',
      attributionLink: 'https://creativecommons.org/licenses/by-sa/4.0'
    },
    {
      year: '1969',
      title: 'Habitat Range Mapped',
      description: 'Scientists map vaquita distribution',
      type: 'research',
      icon: Search,
      isMap: true,
      mapUrl: 'https://www.google.com/maps/d/embed?mid=1Iyhf0B5OcpcGMvMsPwI5sbm9I95jDsk&ehbc=2E312F',
      details: 'Researchers conduct the first systematic surveys to map vaquita distribution, confirming they exist only in the northern Gulf of California. The habitat environment is identified as shallow, murky waters rich in nutrients.',
      significance: 'Habitat boundaries established',
      population: 'Estimated 400-500',
      location: 'Upper Gulf of California',
      impact: 'Defined conservation area needs',
      color: 'bg-green-600',
    },
    {
      year: '1975',
      title: 'Totoaba Fishing Banned',
      description: 'Mexico bans all totoaba fishing',
      type: 'protection',
      icon: Shield,
      image: 'https://upload.wikimedia.org/wikipedia/commons/a/a0/Totoaba.jpg',
      details: 'Mexico banned all totoaba fishing in 1975 due to overexploitation. Vaquita are often killed by totoaba or shrimp bycatching. Totoaba had been fished into near extinction and the ban marks Mexico\'s first attempt to address the issue.',
      significance: 'First fishing regulation to protect vaquita',
      population: 'Estimated 400-500',
      location: 'Northern Gulf of California',
      impact: 'Initial regulatory response to bycatch',
      color: 'bg-purple-600',
      attribution: 'Public domain, via Wikimedia Commons',
      attributionLink: 'https://www.fisheries.noaa.gov/species/totoaba'
    },
    {
      year: '1985',
      title: 'U.S. Endangered Species Listing',
      description: 'Vaquita added to U.S. ESA',
      type: 'protection',
      icon: BookOpen,
      image: 'https://upload.wikimedia.org/wikipedia/commons/d/d9/Endangered_species_pie_chart_2020_USA.jpg',
      details: 'On January 9, 1985, the U.S National Marine Fisheries Service adds vaquita to the U.S. Endangered Species Act list, effective February 8, 1985.',
      significance: 'U.S. legal protection established',
      population: 'Estimated 300-400',
      location: 'United States',
      impact: 'International recognition of threat',
      color: 'bg-blue-600',
      attribution: 'Congressional Research Service, Public domain, via Wikimedia Commons',
      attributionLink: 'https://commons.wikimedia.org/wiki/'
    },
    {
      year: '1990',
      title: 'IUCN Uplists to Endangered',
      description: 'Vaquita status changed to Endangered',
      type: 'crisis',
      icon: AlertTriangle,
      image: 'https://upload.wikimedia.org/wikipedia/commons/3/3c/Status_iucn3.1_LC_ast_fix.svg',
      details: 'The IUCN uplists vaquita to "Endangered" status on the red list. Around this time, informal surveys and report showed that around 128 vaquita deaths were documented between 1985 and 1992. ',
      significance: 'Critical status internationally recognized',
      population: 'Estimated 200-300',
      location: 'IUCN Red List',
      impact: 'Global conservation alarm raised',
      color: 'bg-orange-600',
      attribution: 'User:Pengo, CC BY-SA 2.5, via Wikimedia Commons',
      attributionLink: 'https://creativecommons.org/licenses/by-sa/2.5'
    },
    {
      year: '1991',
      title: 'Mexico Joins CITES',
      description: 'Vaquita added to CITES Appendix I',
      type: 'protection',
      icon: Shield,
      image: 'https://upload.wikimedia.org/wikipedia/commons/1/18/Mx-map.png?20110401004835',
      details: 'Mexico joins the Convention on International Trade in Endangered Species (CITES), and vaquita is listed in Appendix I, which provides protections for vaquitas and totoaba.',
      significance: 'International legal protection',
      population: 'Estimated 200-250',
      location: 'International treaty',
      impact: 'Legal framework for protection',
      color: 'bg-blue-600',
    },
    {
      year: '1997',
      title: 'First Population Survey',
      description: 'Scientific baseline established: ~600 individuals',
      type: 'milestone',
      icon: Users,
      image: 'https://upload.wikimedia.org/wikipedia/commons/9/94/Vaquita3_Olson_NOAA.jpg',
      details: 'The first comprehensive scientific survey is conducted. The 1997 line-transect survey (a joint Mexico-U.S. effort) estimates a population of about 567 vaquitas (95% CI ~177–1,073) living in the Upper Gulf',
      significance: 'Scientific baseline established',
      population: '~600 individuals',
      location: 'Northern Gulf of California',
      impact: 'Baseline for monitoring decline',
      color: 'bg-green-600',
      attribution: 'Paula Olson, NOAA, Public domain, via Wikimedia Commons',
    },
    {
      year: '2000',
      title: 'Biosphere Reserve Law',
      description: 'Bycatch prohibition enacted',
      type: 'protection',
      icon: FileText,
      image: 'https://upload.wikimedia.org/wikipedia/commons/5/52/Ed_Fletcher_and_the_totaba_he_caught.jpg',
      details: 'Under General Law of Ecological Equilibrium (effective 2000), the Mexican government prohibits any fishery within the Biosphere Reserve(seen in the map) that causes bycatch of listed species. ',
      significance: 'Legal prohibition of bycatch',
      population: 'Estimated 400-500',
      location: 'Upper Gulf Biosphere Reserve',
      impact: 'Stricter fishing regulations',
      color: 'bg-purple-600',
      attribution: 'Los Angeles Times, CC BY 4.0 , via Wikimedia Commons',
      attributionLink: 'https://creativecommons.org/licenses/by/4.0'
    },
    {
      year: '2005',
      title: 'Vaquita Refuge Established',
      description: 'Mexico creates official protected area',
      icon: Search,
      isMap: true,
      mapUrl: 'https://www.google.com/maps/d/embed?mid=1Iyhf0B5OcpcGMvMsPwI5sbm9I95jDsk&ehbc=2E312F',
      details: 'The Mexican government establishes the Vaquita Refuge (Área de Refugio para la Protección de la Vaquita), a protected area covering 12,000 km² in the northern Gulf of California. The vaquita\'s habitat is also included in the UNESCO World Heritage Site.',
      significance: 'Legal protection zone created',
      population: 'Estimated 200-300',
      location: 'Northern Gulf of California',
      impact: 'Protected area established',
      color: 'bg-purple-600',
    },
    {
      year: '2008',
      title: 'Population Drops Below 250',
      description: 'Surveys show alarming decline',
      type: 'decline',
      icon: TrendingDown,
      image: '/Photos/Graph.png',
      details: 'Mexico establishes the Species Conservation Action Program for Vaquita, also known as PACE-Vaquita. In fall 2008, a second full sighting survey is conducted, estimating only 245 vaquitas remain (95% CI ~90–670). This represents a dramatic decline of 57% decline from 1997.',
      significance: 'Critical population threshold crossed',
      population: '~245 individuals',
      location: 'Vaquita habitat',
      impact: 'Increased urgency for conservation',
      color: 'bg-orange-600',
    },
    {
      year: '2013',
      title: 'Advisory Commission Created',
      description: 'Presidential commission established',
      type: 'action',
      icon: Users,
      image: 'https://upload.wikimedia.org/wikipedia/commons/d/d7/Ciudad.de.Mexico.City.Distrito.Federal.DF.Paseo.Reforma.Skyline.jpg',
      details: 'The Mexican President announces an Advisory Commission for the Recovery of the Vaquita. Later that year, new regulation (NOM-002-SAG/PESC) to ban gillnet fishing for shrimps',
      significance: 'High-level government action',
      population: '~163 individuals',
      location: 'Northern Gulf',
      impact: 'Presidential-level intervention',
      color: 'bg-blue-600',
      attribution: 'Alejandro Islas Photograph AC, CC BY 2.0 , via Wikimedia Commons',
      attributionLink: 'https://creativecommons.org/licenses/by/2.0'
    },
    {
      year: '2014',
      title: 'Emergency Action Plan',
      description: 'Mexico announces comprehensive rescue plan',
      type: 'action',
      icon: Zap,
      image: '/Photos/Plan.png',
      details: 'Scientist estimate that only around 97 individuals left in 2014. Over $30 million invested by Mexico in vaquita conservation but CIRVA warns that half of the vaquita population was lost in just the past three years.',
      significance: 'Major conservation investment',
      population: '~100 individuals',
      location: 'Mexico City/Gulf of California',
      impact: 'Massive funding commitment',
      color: 'bg-orange-600',
      attribution: 'Attribution needed - via Wikimedia Commons',
      attributionLink: 'https://commons.wikimedia.org/wiki/File:Mexican_government_announcement.jpg'
    },
    {
      year: '2015',
      title: 'Gillnet Ban Announced',
      description: 'Mexico bans gillnets in vaquita habitat',
      type: 'protection',
      icon: Shield,
      image: 'https://www.flickr.com/photo_download.gne?id=53115723887&secret=0f7bc5a7f7&size=o&source=photoPageEngagement',
      details: 'In April of 2015,Mexico announces a two-year ban on gillnet fishing in the vaquita refuge area. An international survey produces a new population estimate of about 59 vaquitas remaining. It shows that 7 out of 10 vaquitas have died in the past since 2008',
      significance: 'Historic fishing restrictions',
      population: '~60 individuals',
      location: 'Vaquita Refuge',
      impact: 'Legal ban on primary threat',
      color: 'bg-purple-600',
      attribution: 'Paolo Gamba, CC BY 2.0, via Flickr',
      attributionLink: 'https://creativecommons.org/licenses/by/2.0/deed.en'
    },
    {
      year: '2017',
      title: 'VaquitaCPR Attempt',
      description: 'Rescue and breeding program launched',
      type: 'rescue',
      icon: Ship,
      image: 'https://www.vaquitacpr.org/wp-content/uploads/2017/09/Vaquita-Logo-English-Color.jpg',
      details: 'VaquitaCPR (Conservation, Protection, and Recovery) attempts to capture vaquitas for a breeding program. After one vaquita dies from capture stress, the program is suspended, highlighting the species\' extreme sensitivity.',
      significance: 'Captive breeding attempt fails',
      population: '~30 individuals',
      location: 'Gulf of California',
      impact: 'Confirmed in-situ conservation only option',
      color: 'bg-red-600',
      attribution: 'Vaquita CPR Website',
      attributionLink: 'https://www.vaquitacpr.org/'
    },
    {
      year: '2018',
      title: 'Fewer Than 20 Left',
      description: 'Population crashes to critical level',
      type: 'crisis',
      icon: AlertTriangle,
      image: '/Photos/graph2.png',
      details: 'Surveys reveal fewer than 20 vaquitas remain. Scientists declare the species functionally extinct in the wild without immediate elimination of all gillnets.',
      significance: 'Functional extinction threshold',
      population: '~19 individuals',
      location: 'Remnant habitat',
      impact: 'Extinction imminent without action',
      color: 'bg-red-600',
    },
    {
      year: '2019',
      title: 'Permanent Gillnet Ban',
      description: 'Mexico makes gillnet ban permanent',
      type: 'protection',
      icon: Shield,
      image: 'https://www.flickr.com/photo_download.gne?id=11179825295&secret=3d5e43b97c&size=l&source=photoPageEngagement',
      details: 'The Mexican government makes the gillnet ban permanent across the entire Upper Gulf. However, illegal fishing continues due to insufficient enforcement and high totoaba bladder prices.',
      significance: 'Permanent legal protection',
      population: '~10 individuals',
      location: 'Upper Gulf of California',
      impact: 'Permanent threat ban (on paper)',
      color: 'bg-purple-600',
      attribution: 'Lauren Packard, CC BY 2.0, via Flickr',
      attributionLink: 'https://creativecommons.org/licenses/by/2.0/deed.en'
    },
    {
      year: '2021',
      title: 'Illegal Gillnets Found',
      description: 'Extensive illegal fishing discovered',
      type: 'threat',
      icon: AlertTriangle,
      image: 'https://www.flickr.com/photo_download.gne?id=4128292809&secret=2fd4d47587&size=h&source=photoPageEngagement',
      details: 'In November 3rd, 2021, 117 pangas (small fishing boats) are found inside the zero tolerance area and 43 are found outside of the area, all using gillnets. ',
      significance: 'Enforcement failure revealed',
      population: '~10 individuals',
      location: 'Vaquita habitat',
      impact: 'Evidence of ongoing threat',
      color: 'bg-red-600',
      attribution: 'medea_material, CC BY-SA 2.0, via Flickr',
      attributionLink: 'https://creativecommons.org/licenses/by/2.0/deed.en'
    },
    {
      year: '2023',
      title: 'Enhanced Enforcement',
      description: 'Physical barriers deployed',
      type: 'action',
      icon: Shield,
      image: '/Photos/Block.png',
      details: 'Following the gillnet findings in 2023, the Mexican Government responds by placing metal blocks and hooks to stop gillnet fishing from happening. In 2023, the Sea Shepherd Conservation Society conducted a survey in finding the vaquitas.',
      significance: 'New enforcement strategy',
      population: '~10 individuals',
      location: 'Core habitat area',
      impact: 'Physical deterrents installed',
      color: 'bg-purple-600',
    },
    {
      year: '2024',
      title: 'Hope Spotted',
      description: 'Calf and pod sighted',
      type: 'hope',
      icon: Award,
      image: 'https://www.flickr.com/photo_download.gne?id=26121277935&secret=e5d7cf043e&size=k&source=photoPageEngagement',
      details: 'Sea Shepherd finds an about 14 month old calf with mum vaquita in their May 2024 survey. It gives hope because the first year is usually the toughest year for a vaquita to survive. On the last survey day of May 23th 2024, they find 4 vaquitas together.',
      significance: 'Evidence of reproduction',
      population: '<10 individuals',
      location: 'Vaquita refuge',
      impact: 'Survival and breeding confirmed',
      color: 'bg-green-600',
    },
    {
      year: '2025',
      title: 'Now',
      description: 'Awaiting survey results',
      type: 'monitoring',
      icon: Search,
      image: 'https://www.flickr.com/photo_download.gne?id=21684568558&secret=1aee9bd382&size=l&source=photoPageEngagement',
      details: 'Acoustic and visual survey will be ready on November 7th, 2025 and the papers will be submitted in early 2026.',
      significance: 'Current status assessment',
      population: '<10 individuals',
      location: 'Small remnant area',
      impact: 'Future direction uncertain',
      color: 'bg-blue-600',
    }
  ];

  return (
    <div className="bg-gray-900 rounded-lg p-8 border border-gray-700">
      {/* Timeline Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h3 className="text-2xl font-bold text-white mb-2">Conservation Timeline</h3>
          <p className="text-gray-400">Click on events to see detailed information</p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={scrollLeft}
            className="p-2 bg-gray-700 hover:bg-gray-600 rounded-full text-white transition-colors"
            aria-label="Scroll left"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            onClick={scrollRight}
            className="p-2 bg-gray-700 hover:bg-gray-600 rounded-full text-white transition-colors"
            aria-label="Scroll right"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Horizontal Scrollable Timeline */}
      <div className="relative">
        <div 
          ref={scrollRef}
          className="flex space-x-6 overflow-x-auto scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800 pb-4"
          style={{ scrollbarWidth: 'thin' }}
        >
          {timelineEvents.map((event, index) => (
            <div
              key={index}
              className="flex-shrink-0 relative"
            >
              {/* Timeline Line */}
              {index < timelineEvents.length - 1 && (
                <div className="absolute top-6 left-16 w-20 h-0.5 bg-gray-600 z-0"></div>
              )}
              
              {/* Timeline Event */}
              <div 
                className="relative z-10 flex flex-col items-center group cursor-pointer"
                onClick={() => handleEventClick(event)}
              >
                {/* Event Icon */}
                <div className={`w-12 h-12 ${event.color} rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg border-4 ${selectedEvent === event ? 'border-white' : 'border-gray-900'}`}>
                  <event.icon className="h-6 w-6 text-white" />
                </div>
                
                {/* Year */}
                <div className="mt-2 text-white font-bold text-lg">{event.year}</div>
                
                {/* Event Title */}
                <div className="mt-1 text-center max-w-32">
                  <h4 className="text-sm font-semibold text-gray-300 group-hover:text-white transition-colors leading-tight">
                    {event.title}
                  </h4>
                </div>
                
                {/* Population indicator */}
                <div className="mt-2 text-xs text-gray-500 text-center">
                  {event.population.split(' ')[0]}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Detailed Information Panel - Always visible, shows content when event is selected */}
      <div className="mt-8 bg-gray-800 rounded-lg border border-gray-600 transition-all duration-300" style={{ minHeight: '400px' }}>
        {selectedEvent ? (
          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Event Details */}
              <div>
                <div className="flex items-center space-x-3 mb-4">
                  <div className={`w-10 h-10 ${selectedEvent.color} rounded-full flex items-center justify-center`}>
                    <selectedEvent.icon className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-white">{selectedEvent.year} - {selectedEvent.title}</h4>
                    <p className="text-gray-400 text-sm">{selectedEvent.significance}</p>
                  </div>
                </div>
                
                <p className="text-gray-300 leading-relaxed mb-4">{selectedEvent.details}</p>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-400">Population:</span>
                    <span className="text-white ml-2 font-semibold">{selectedEvent.population}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Location:</span>
                    <span className="text-white ml-2">{selectedEvent.location}</span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-gray-400">Impact:</span>
                    <span className="text-white ml-2">{selectedEvent.impact}</span>
                  </div>
                </div>
              </div>
              
              {/* Event Image or Map */}
              <div>
                {selectedEvent.isMap ? (
                  <iframe 
                    src={selectedEvent.mapUrl}
                    width="100%" 
                    height="400"
                    style={{ border: 0, borderRadius: '0.5rem' }}
                    allowFullScreen
                    loading="lazy"
                    title="Vaquita Habitat Map"
                    className="shadow-lg"
                  />
                ) : (
                  <>
                    <img 
                      src={selectedEvent.image}
                      alt={`${selectedEvent.title}`}
                      className="w-full h-64 object-cover rounded-lg shadow-lg"
                    />
                    <p className="text-gray-500 text-xs mt-2 text-center">
                      <a 
                        href={selectedEvent.attributionLink} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="hover:text-gray-300 hover:underline"
                      >
                        {selectedEvent.attribution}
                      </a>
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full p-6">
            <div className="text-center text-gray-400">
              <Calendar className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg">Click on any event in the timeline to see detailed information</p>
            </div>
          </div>
        )}
      </div>

      {/* Timeline Legend */}
      <div className="mt-6 flex flex-wrap gap-4 justify-center">
        <div className="flex items-center space-x-2 text-sm">
          <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
          <span className="text-gray-400">Discovery/Research</span>
        </div>
        <div className="flex items-center space-x-2 text-sm">
          <div className="w-3 h-3 bg-green-600 rounded-full"></div>
          <span className="text-gray-400">Conservation Success</span>
        </div>
        <div className="flex items-center space-x-2 text-sm">
          <div className="w-3 h-3 bg-purple-600 rounded-full"></div>
          <span className="text-gray-400">Protection Measures</span>
        </div>
        <div className="flex items-center space-x-2 text-sm">
          <div className="w-3 h-3 bg-yellow-600 rounded-full"></div>
          <span className="text-gray-400">Early Warnings</span>
        </div>
        <div className="flex items-center space-x-2 text-sm">
          <div className="w-3 h-3 bg-orange-600 rounded-full"></div>
          <span className="text-gray-400">Crisis Events</span>
        </div>
        <div className="flex items-center space-x-2 text-sm">
          <div className="w-3 h-3 bg-red-600 rounded-full"></div>
          <span className="text-gray-400">Population Decline</span>
        </div>
      </div>
    </div>
  );
};

export default InteractiveTimeline;